import { useCallback, useEffect, useState } from 'react';
import { IconMonitor, IconMoon, IconSun } from './icons';

/**
 * Тема интерфейса.
 *
 * Состояний три, а не два: «как в системе» — это отдельный режим, а не синоним
 * светлой. Управляющий открывает админку днём с ноутбука и вечером с планшета в
 * мойке; система знает про это больше, чем мы, поэтому по умолчанию слушаем её
 * и переключаемся на лету, если пользователь не выбрал тему руками.
 */
export type ThemeMode = 'system' | 'light' | 'dark';

const KEY = 'moika.theme';

function read(): ThemeMode {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    return 'system';
  }
}

function apply(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
}

export interface ThemeApi {
  readonly mode: ThemeMode;
  readonly setMode: (mode: ThemeMode) => void;
  /** Какая тема сейчас на экране — с учётом системной. */
  readonly resolved: 'light' | 'dark';
}

export function useTheme(): ThemeApi {
  const [mode, setModeState] = useState<ThemeMode>(read);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    apply(next);
    try {
      if (next === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      /* приватный режим: тема продержится до перезагрузки */
    }
  }, []);

  useEffect(() => {
    apply(mode);
  }, [mode]);

  return { mode, setMode, resolved: mode === 'system' ? (systemDark ? 'dark' : 'light') : mode };
}

const ORDER: readonly ThemeMode[] = ['system', 'light', 'dark'];

const LABEL: Readonly<Record<ThemeMode, string>> = {
  system: 'как в системе',
  light: 'светлая',
  dark: 'тёмная',
};

/**
 * Переключатель — одна кнопка, а не три: в шапке дорог каждый пиксель, а режимов
 * всего три, и цикл по ним предсказуем. Текущий режим назван словами в подсказке
 * и в aria-label, иначе по одной иконке «луна» не понять, тёмная тема сейчас
 * включена или будет включена по нажатию.
 */
export function ThemeToggle({ theme }: { theme: ThemeApi }) {
  const next = ORDER[(ORDER.indexOf(theme.mode) + 1) % ORDER.length] ?? 'system';
  const Glyph = theme.mode === 'system' ? IconMonitor : theme.mode === 'light' ? IconSun : IconMoon;

  return (
    <button
      type="button"
      onClick={() => theme.setMode(next)}
      title={`Тема: ${LABEL[theme.mode]}. Нажмите, чтобы включить: ${LABEL[next]}`}
      aria-label={`Тема: ${LABEL[theme.mode]}. Переключить на: ${LABEL[next]}`}
      className="flex h-9 w-9 items-center justify-center rounded-control text-ink-muted transition hover:bg-sunken hover:text-ink"
    >
      <Glyph size={18} />
    </button>
  );
}
